﻿import React, { useState, useEffect } from 'react';
import { CoaMobilePhaseDatum } from '../models/coa-mobile-phase-datum';
import { Entities } from '../models/enums';
import { CrudFunctions } from '../utilities/crud-functions';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { ItemBasicInformation } from '../models/item-basic-information';
import AuditHistoryComponent from './AuditHistoryComponent';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';

import DataGrid, {
    Column,
    Paging,
    Editing,
    Button as ColumnButton,
    Toolbar,
    Lookup,
    Item as ToolbarItem,
    LoadPanel
} from 'devextreme-react/data-grid';

import { Popup as DialogPopup } from 'devextreme-react/popup';

const phases = ["A", "B", "C", "D"];

let key = 1;

export interface Props {
    benchSheetId?: string;
    isReadOnly: boolean;
    showRatioColumn: boolean;
}

export default function HplcCoaGradientsData(props: Props) {
    const [coaMobilePhaseData, setCoaMobilePhaseData] =
        useState<CoaMobilePhaseDatum[]>([]);

    const [selectedCoaMobilePhaseDatum, setSelectedCoaMobilePhaseDatum] =
        useState<CoaMobilePhaseDatum>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getCoaMobilePhaseData();
    }, [props.benchSheetId]);

    key++;

    return (
        <div>
            <div id="coaMobilePhaseDatagridContainer" className="pw-margin-top-1">
                <DataGrid
                    height="13rem"
                    keyExpr="coaMobilePhaseDatumId"
                    dataSource={coaMobilePhaseData ?
                        coaMobilePhaseData : []}
                    onSelectionChanged={coaMobilePhaseDataSelectionChanged}
                    onRowValidating={onCoaMobilePhaseRowValidating}
                    onRowRemoving={onCoaMobilePhaseRowDeleting}
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    showBorders={true} >
                    <LoadPanel enabled={true} />
                    <Paging enabled={false} />

                    <Column type="buttons" width="7rem">
                        <ColumnButton name="edit" />
                        <ColumnButton name="delete" />
                        <ColumnButton hint="audit information" icon="clock"
                            onClick={onCoaGradientAuditButtonClicked} />
                        <ColumnButton hint="information" icon="info"
                            onClick={onCoaGradientInformationButtonClicked} />
                    </Column>

                    <Column dataField='phase' dataType="string" width='10rem' >
                        <Lookup dataSource={phases}
                            allowClearing={false} />
                    </Column>

                    <Column dataField='ratio' dataType="number" width='10rem'
                        visible={props.showRatioColumn} />
                    <Column dataField='value' dataType="string" />

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">COA Mobile Phase Data</div>
                            </div>
                        </ToolbarItem>
                        <ToolbarItem location='after' name="addRowButton" />
                    </Toolbar>

                    <Editing
                        mode="row" newRowPosition="last"
                        useIcons={true}
                        allowAdding={!props.isReadOnly}
                        allowUpdating={!props.isReadOnly}
                        allowDeleting={!props.isReadOnly} >
                    </Editing>

                </DataGrid>

            </div>

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function coaMobilePhaseDataInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            setSelectedCoaMobilePhaseDatum(e.row.data);
            showReadingInformationDialog(e.row.data);
        }
    }

    function coaMobilePhaseDataSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            setSelectedCoaMobilePhaseDatum(e.selectedRowsData[0]);
        }
    }

    async function onCoaMobilePhaseRowValidating(e: any) {

        if (e.oldData) {
            let coaMobilePhaseDatum = e.oldData as CoaMobilePhaseDatum;

            if (coaMobilePhaseDatum) {
                let updatedCoaMobilePhaseDatum = { ...coaMobilePhaseDatum, ...e.newData };

                await updateCoaMobilePhaseDatum(updatedCoaMobilePhaseDatum)
                    .then(() => {
                        //  showToast("Glassware Item updated", "success");
                        getCoaMobilePhaseData();
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating COA Mobile Phase Data");
                    });
            }
        }
        else {
            if (e.newData) {
                let coaMobilePhaseDatum = e.newData as CoaMobilePhaseDatum;
                coaMobilePhaseDatum.benchSheetId = props.benchSheetId;

                await insertCoaMobilePhaseDatum(coaMobilePhaseDatum)
                    .then(() => {
                        //showToast("Glassware Item saved", "success");
                        getCoaMobilePhaseData();
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Inserting COA Mobile Phase Data");
                    });
            }
        }
    }

    async function onCoaMobilePhaseRowDeleting(e: any) {
        if (e.data && e.data.coaMobilePhaseDatumId) {
            await deleteCoaMobilePhaseDatum(e.data.coaMobilePhaseDatumId)
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting COA Mobile Phase Data");
                });
        }
    }

    async function getCoaMobilePhaseData() {
        await CrudFunctions.getData(`benchsheets/coamobilephasedata/${props.benchSheetId}`)
            .then((data) => {
                setCoaMobilePhaseData(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving COA Mobile Phase Data");
            });
    }

    async function insertCoaMobilePhaseDatum(coaMobilePhaseDatum: CoaMobilePhaseDatum) {
        return await CrudFunctions.postData("benchsheets/coamobilephasedatum", coaMobilePhaseDatum);
    }

    async function updateCoaMobilePhaseDatum(coaMobilePhaseDatum: CoaMobilePhaseDatum) {
        return await CrudFunctions.putData("benchsheets/coamobilephasedatum", coaMobilePhaseDatum);
    }

    async function deleteCoaMobilePhaseDatum(coaMobilePhaseDatumId: string) {
        return await CrudFunctions.deleteData(`benchsheets/coamobilephasedatum/${coaMobilePhaseDatumId}`);
    }

    function showReadingInformationDialog(coaMobilePhaseDatum: CoaMobilePhaseDatum) {
        if (coaMobilePhaseDatum) {
            let itemInformation: ItemBasicInformation = {
                rowPointer: coaMobilePhaseDatum.coaMobilePhaseDatumId,
                createTimestamp: coaMobilePhaseDatum.createTimestamp,
                lastModifiedTimestamp: coaMobilePhaseDatum.modifyTimestamp,
                createUser: coaMobilePhaseDatum.createUser,
                modifyUser: coaMobilePhaseDatum.modifyUser
            };

            setItemBasicInformation(itemInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function onCoaGradientAuditButtonClicked(e: any) {

    }

    function onCoaGradientInformationButtonClicked(e: any) {

    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

}